import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";

type AuthState = "loading" | "authorized" | "unauthorized";

function isAdminSession(session: Session | null): boolean {
  return session?.user?.user_metadata?.role === "admin";
}

export default function AdminGuard({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [redirectReason, setRedirectReason] = useState<string | null>(null);
  const authStateRef = useRef<AuthState>("loading");

  useEffect(() => {
    let mounted = true;

    const applySession = (session: Session | null, reason?: string) => {
      if (!mounted) return;

      if (!session) {
        authStateRef.current = "unauthorized";
        setRedirectReason(reason ?? "no_session");
        setAuthState("unauthorized");
        return;
      }

      if (!isAdminSession(session)) {
        authStateRef.current = "unauthorized";
        setRedirectReason("not_admin");
        setAuthState("unauthorized");
        return;
      }

      authStateRef.current = "authorized";
      setAuthState("authorized");
    };

    // Never call getSession/getUser inside onAuthStateChange — it deadlocks and
    // retriggers auth events (looks like the page "refreshing" every few seconds).
    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(() => {
        if (!mounted) return;

        if (event === "SIGNED_OUT") {
          applySession(null, "signed_out");
          return;
        }

        if (
          event === "SIGNED_IN" ||
          event === "USER_UPDATED" ||
          event === "TOKEN_REFRESHED" ||
          event === "INITIAL_SESSION"
        ) {
          applySession(session);
        }
      }, 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  if (authState === "loading") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Checking authentication...</p>
      </div>
    );
  }

  if (authState === "unauthorized") {
    if (redirectReason) {
      console.warn("AdminGuard denied access:", redirectReason);
    }
    const params = redirectReason === "not_admin" ? "?error=not_admin" : "";
    return <Navigate to={`/admin/login${params}`} replace />;
  }

  return <>{children}</>;
}
